/**
 * Convert an unknown error (fetch failure, Firebase Auth error, API error body,
 * thrown string, etc.) into a message safe to show to end users.
 *
 * Raw `error.message` values often leak internals ("Firebase: Error
 * (auth/invalid-credential)", "Failed to fetch"), so known cases are mapped to
 * friendly copy and anything unrecognized falls back to the provided default.
 */
export const getUserFacingError = (
  error: unknown,
  fallback = 'Something went wrong. Please try again.'
): string => {
  if (!error) return fallback;

  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? error.message
        : typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : '';

  if (!message) return fallback;

  // Firebase Auth error codes (message looks like "Firebase: Error (auth/...)").
  const firebaseCode =
    (error as { code?: string }).code || message.match(/\(auth\/([\w-]+)\)/)?.[0];
  if (firebaseCode) {
    const code = firebaseCode.replace(/[()]/g, '');
    const authErrors: Record<string, string> = {
      'auth/invalid-credential': 'Incorrect email or password.',
      'auth/user-not-found': 'Incorrect email or password.',
      'auth/wrong-password': 'Incorrect email or password.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/too-many-requests':
        'Too many attempts. Please wait a few minutes and try again.',
      'auth/network-request-failed':
        'Network error. Please check your connection and try again.',
      'auth/popup-closed-by-user': 'Sign-in was cancelled.',
      'auth/requires-recent-login':
        'For security, please sign in again before making this change.',
      'auth/unauthorized-continue-uri':
        'Email verification is not available for this site yet. Contact support.',
      'auth/invalid-continue-uri':
        'Email verification is not available for this site yet. Contact support.',
    };
    if (authErrors[code]) return authErrors[code];
    if (code.startsWith('auth/')) return fallback;
  }

  // Network-level failures from fetch.
  if (/failed to fetch|networkerror|load failed/i.test(message)) {
    return 'Unable to reach the server. Please check your connection and try again.';
  }

  // Messages that look like internals (stack traces, JSON blobs, HTML) are unsafe to show.
  if (message.length > 200 || /<\/?[a-z]+>|\{"/.test(message)) {
    return fallback;
  }

  return message;
};
